import { get } from 'lodash';
import Quill, { Delta } from 'quill';
import ToolbarModule from 'quill/modules/toolbar';

import { createDropdownMenu, positionDropdown } from './common-toolbar-utils';
import { convertQuillContentsToText, replaceQuillVariablesWithActualValues } from './quill-templates.helpers';
import { VARIABLE_BLOT_NAME, VariableBlot } from './variable.blot';

export enum QuillVariableType {
  TEXT = 'text',
  DATE = 'date',
  CURRENCY = 'currency',
}

export interface QuillVariable {
  title: string;
  description?: string;
  dataKey: string;
  dataType?: QuillVariableType;
  color?: string;
  default?: string;
  format?: string;
  value?: any;
}

export class VariableToolbarQuillModule {
  private quill: Quill;
  private variableButton: HTMLButtonElement | null = null;
  private variableMenu: HTMLDivElement | null = null;
  private variables: QuillVariable[] = [];
  data: any = {};

  constructor(quill: Quill, options: { hideVariableButton?: boolean} = {}) {
    this.quill = quill;
    if(!options.hideVariableButton) {
      this.addVariableButton();
    }
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleOutsideClick);
    this.addVariableClipboardMatcher();
    this.quill.root.addEventListener('keydown', this.handleHashKey);
  }

  private addVariableClipboardMatcher(): void {
    this.quill.clipboard.addMatcher(VariableBlot.tagName, (node: HTMLElement, delta: Delta) => {
      if (node.getAttribute && node.getAttribute('data-variable-blot') === 'true') {
        return new Delta().insert({
          variable: {
            title: node.getAttribute('data-title') || '',
            dataKey: node.getAttribute('data-key') || '',
            dataType: node.getAttribute('data-type') || '',
            format: node.getAttribute('data-format') || '',
            value: get(this.data, node.getAttribute('data-key')) || '',
            color: node.getAttribute('data-color') || '#d1d1d1',
          }
        });
      }
      return delta;
    });
  }

  private addVariableButton(): void {
    const toolbar = this.getToolbarModule()?.container;
    if (!toolbar) return;

    this.variableButton = document.createElement('button');
    this.variableButton.innerHTML = '🏷️';
    this.variableButton.classList.add('ql-variable-button');
    this.variableButton.addEventListener('click', () => this.toggleMenu());

    toolbar.querySelector('.ql-formats')?.appendChild(this.variableButton);
  }

  private toggleMenu(): void {
    const editorContainer = this.quill.root.parentElement as HTMLElement;
    if (!editorContainer) return;

    if (this.variableMenu) {
      this.variableMenu.remove();
      this.variableMenu = null;
      return;
    }

    if (!this.variables.length) return;

    this.variableMenu = createDropdownMenu(
      this.variables.map((v) => v.title),
      (selected) => {
        const variable = this.variables.find((v) => v.title === selected);
        if (variable) this.insertVariableBlot(variable);
        this.variableMenu?.remove();
        this.variableMenu = null;
      }
    );

    document.body.appendChild(this.variableMenu);
    positionDropdown(
      this.variableMenu,
      this.variableButton!,
      document.body
    );
  }

  private insertVariableBlot(variable: QuillVariable): void {
    const range = this.quill.getSelection(true);

	const value = get(this.data, variable.dataKey) || '';

    const blotData: QuillVariable = {
      ...variable,
      dataType: variable.dataType || QuillVariableType.TEXT,
      color: variable.color || '#d1d1d1',
	    value,
    };

    const index = range?.index || 0;
    this.quill.insertEmbed(index, VARIABLE_BLOT_NAME, blotData, Quill.sources.USER);

    //Take the cursor to the end of the inserted variable
    this.quill.setSelection(index + 1, Quill.sources.SILENT);
  }

  public updateVariables(variables: QuillVariable[]): void {
    this.variables = variables;
    if (variables.length === 0 && this.variableButton) {
      this.variableButton.remove();
      this.variableButton = null;
    }
  }

  public updateData(data: Record<string, any>): void {
    this.data = data;
    this.refreshVariableValuesInContents();
  }

  public refreshVariableValuesInContents(): void {
    const contents = this.quill.getContents();
    const updatedOps = contents.ops.map((op) => {
        if (op?.insert?.[VARIABLE_BLOT_NAME]) {
            const variableDetails = op.insert[VARIABLE_BLOT_NAME] as QuillVariable;

            // Update the value with formatted data
            return {
                ...op,
                insert: {
                    [VARIABLE_BLOT_NAME]: {
                        ...variableDetails,
                        value: get(this.data, variableDetails.dataKey),
                    },
                },
            };
        }
        return op;
    });

    this.quill.setContents(updatedOps, Quill.sources.SILENT);
  }

  /**
   * Returns the Quill contents and plain text with all variables replaced by their actual values.
   */
  public getResolvedContentsAndText(): { contents: Delta; text: string } {
    const resolvedContents = replaceQuillVariablesWithActualValues(
      this.quill.getContents(),
      this.data
    );
    return {
      contents: resolvedContents,
      text: convertQuillContentsToText(resolvedContents),
    };
  }

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.variableMenu) {
      this.variableMenu.remove();
      this.variableMenu = null;
    }
  };

  private handleOutsideClick = (event: MouseEvent): void => {
    if (
      this.variableMenu &&
      !this.variableMenu.contains(event.target as Node) &&
      this.variableButton !== event.target
    ) {
      this.variableMenu.remove();
      this.variableMenu = null;
    }
  };

  /**
   * Helper to create and show the variable menu at a given position (x, y) or at the cursor.
   * If x and y are provided, menu is shown at those coordinates; otherwise, at the cursor.
   */
  private showVariableMenu({ x, y }: { x?: number; y?: number } = {}): void {
    if (!this.variables.length) return;

    const editorContainer = this.quill.root.parentElement as HTMLElement;
    if (!editorContainer) return;

    if (this.variableMenu) {
      this.variableMenu.remove();
      this.variableMenu = null;
    }

    this.variableMenu = createDropdownMenu(
      this.variables.map((v) => v.title),
      (selected) => {
        const variable = this.variables.find((v) => v.title === selected);
        if (variable) this.insertVariableBlot(variable);
        this.variableMenu?.remove();
        this.variableMenu = null;
      }
    );


    if (typeof x === 'number' && typeof y === 'number') {
        // Position at mouse coordinates
        this.variableMenu.style.position = 'absolute';
        this.variableMenu.style.left = `${x - document.body.getBoundingClientRect().left}px`;
        this.variableMenu.style.top = `${y - document.body.getBoundingClientRect().top}px`;
    } else {
        // Position at cursor
        const range = this.quill.getSelection(true);
        if (!range) return;
        const bounds = this.quill.getBounds(range.index);
        this.variableMenu.style.position = 'absolute';
        this.variableMenu.style.left = `${editorContainer.getBoundingClientRect().left + bounds.left}px`;
        this.variableMenu.style.top = `${editorContainer.getBoundingClientRect().top + bounds.top + bounds.height}px`;
    }
    this.variableMenu.style.zIndex = '1000';
    document.body.appendChild(this.variableMenu);
  }

  // Show variable menu at cursor position when # is pressed
  private handleHashKey = (event: KeyboardEvent): void => {
    if (event.key === '#') {
      event.preventDefault();
      this.showVariableMenu();
    }
  };

  private getToolbarModule() {
    return this.quill.getModule('toolbar') as ToolbarModule;
  }
}
